/* Admin · Vendor Intake Process manager.
 * /admin/vendor-intake
 *
 * Mirror-view of every vendor's intake process with admin authority to:
 *  - Review submitted intakes (claim, approve+deploy, request changes, reject)
 *  - Override-edit any process at will (admin role bypasses lock state)
 *  - Create a new intake FOR a vendor (admin-authored, optionally deploy immediately)
 *  - Un-deploy / re-deploy
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import { VendorIntakeStageEditor } from "@/components/VendorIntakeStageEditor";
import {
  Plus, Save, CheckCircle2, XCircle, MessageCircleQuestion, Eye,
  Rocket, PauseCircle, Inbox, Loader2,
} from "lucide-react";

const STATUS_PILL = {
  draft:        "bg-slate-100 text-slate-700",
  submitted:    "bg-blue-100 text-blue-800",
  under_review: "bg-amber-100 text-amber-800",
  approved:     "bg-emerald-100 text-emerald-800",
  deployed:     "bg-emerald-600 text-white",
  rejected:     "bg-rose-100 text-rose-700",
  needs_changes:"bg-orange-100 text-orange-800",
};

export default function AdminVendorIntake() {
  const [list, setList] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [canonical, setCanonical] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/vendor-intake-processes").then((r) => setList(r.data || []));

  useEffect(() => {
    load();
    api.get("/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
    api.get("/vendor-intake-processes/canonical-stages").then((r) => setCanonical(r.data?.stages || [])).catch(() => {});
  }, []);

  const filtered = list.filter((p) => filter === "all" ? true : p.status === filter);

  const createForVendor = async () => {
    const vid = window.prompt("Enter vendor ID to author a new intake process for:\n\n" + vendors.map((v) => `${v.id} — ${v.name}`).join("\n"));
    if (!vid) return;
    try {
      const stages = canonical.map((c) => ({
        kind: c.kind, title: c.title, description: c.description,
        notify_email: true, notify_sms: false, notify_in_portal: true, assets: [],
      }));
      const r = await api.post("/vendor-intake-processes", {
        vendor_id: vid,
        name: "Admin-authored intake",
        description: "",
        stages,
      });
      toast.success("Created.");
      await load();
      setSelected(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await api.patch(`/vendor-intake-processes/${selected.id}`, {
        name: selected.name,
        description: selected.description,
        service_id: selected.service_id || null,
        stages: selected.stages,
      });
      setSelected(r.data); load(); toast.success("Saved.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const transition = async (id, action) => {
    try {
      const body = ["request-changes", "reject"].includes(action) ? { admin_notes: notes } : undefined;
      const r = await api.post(`/vendor-intake-processes/${id}/${action}`, body);
      toast.success(`${action.replace("-", " ")}.`);
      setNotes("");
      setSelected(r.data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const vendorName = (vid) => vendors.find((v) => v.id === vid)?.name || vid;

  return (
    <ToolPageShell
      eyebrow="Admin · Vendor intake"
      title="Vendor Intake Process manager"
      intro="A mirror-view of every vendor's intake process with admin authority. Review submissions, request changes, reject, approve and deploy, or author a process directly on behalf of a vendor. Admins can override-edit at any state."
      actions={
        <button
          onClick={createForVendor}
          data-testid="admin-intake-author-btn"
          className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"
        >
          <Plus className="w-3.5 h-3.5" /> Author for vendor
        </button>
      }
    >
      <div className="mb-4 inline-flex items-center gap-1 bg-slate-100 rounded-full p-1 text-xs font-semibold flex-wrap">
        {["all", "submitted", "under_review", "needs_changes", "deployed", "approved", "rejected", "draft"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`admin-intake-filter-${f}`}
            className={`px-3 py-1 rounded-full capitalize transition ${filter === f ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
          >
            {f.replace("_", " ")}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_1.6fr] gap-5">
        <div className="space-y-2">
          {filtered.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
              <Inbox className="w-6 h-6 text-slate-300 mx-auto mb-2" />
              No intake processes match this filter.
            </div>
          )}
          {filtered.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelected(p)}
              data-testid={`admin-intake-card-${p.id}`}
              className={`w-full text-left rounded-2xl border p-3 transition ${selected?.id === p.id ? "border-slate-900 ring-2 ring-slate-900/10" : "border-slate-200 hover:border-slate-400"} bg-white`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="font-semibold text-slate-900 text-sm truncate">{p.name}</div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase font-bold ${STATUS_PILL[p.status]}`}>
                  {p.status.replace("_", " ")}
                </span>
              </div>
              <div className="text-[11px] text-slate-500 truncate">
                {vendorName(p.vendor_id)} {p.created_by_role === "admin" && <span className="ml-1 rounded bg-slate-100 px-1.5 text-[9px] font-bold">ADMIN AUTHORED</span>}
              </div>
            </button>
          ))}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 min-h-[400px]">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-sm text-slate-500 py-10">
              <Eye className="w-6 h-6 text-slate-300 mb-2" />
              Pick an intake process to mirror or override.
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1">
                  <input
                    value={selected.name || ""}
                    onChange={(e) => setSelected({ ...selected, name: e.target.value })}
                    data-testid="admin-intake-name"
                    className="w-full text-xl font-display font-bold text-slate-900 border-0 focus:ring-0 px-0"
                  />
                  <div className="text-[12px] text-slate-500">
                    <span className={`rounded-full px-2 py-0.5 uppercase font-bold ${STATUS_PILL[selected.status]} mr-2`}>
                      {selected.status.replace("_", " ")}
                    </span>
                    Vendor: <b className="text-slate-800">{vendorName(selected.vendor_id)}</b>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={save}
                    disabled={saving}
                    data-testid="admin-intake-save"
                    className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-800 rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-slate-200"
                  >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Save override
                  </button>
                  {["draft", "submitted", "under_review", "needs_changes", "rejected", "approved"].includes(selected.status) && (
                    <button
                      onClick={() => transition(selected.id, "deploy")}
                      data-testid="admin-intake-deploy"
                      className="inline-flex items-center gap-1.5 bg-emerald-600 text-white rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-emerald-700"
                    >
                      <Rocket className="w-3.5 h-3.5" /> Deploy
                    </button>
                  )}
                  {selected.status === "deployed" && (
                    <button
                      onClick={() => transition(selected.id, "undeploy")}
                      data-testid="admin-intake-undeploy"
                      className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-800 rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-slate-200"
                    >
                      <PauseCircle className="w-3.5 h-3.5" /> Un-deploy
                    </button>
                  )}
                </div>
              </div>

              {["submitted", "under_review"].includes(selected.status) && (
                <div className="rounded-xl bg-blue-50 border border-blue-100 p-3 mb-3">
                  <textarea
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid="admin-intake-notes"
                    placeholder="Optional admin note (used by Request changes / Reject)"
                    className="w-full text-sm rounded-lg border border-slate-200 bg-white px-3 py-2 mb-2 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    {selected.status === "submitted" && (
                      <button
                        onClick={() => transition(selected.id, "review")}
                        data-testid="admin-intake-claim"
                        className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-800 rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-slate-200"
                      >
                        <Eye className="w-3.5 h-3.5" /> Start review
                      </button>
                    )}
                    <button
                      onClick={() => transition(selected.id, "approve")}
                      data-testid="admin-intake-approve"
                      className="inline-flex items-center gap-1.5 bg-emerald-600 text-white rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-emerald-700"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Approve & deploy
                    </button>
                    <button
                      onClick={() => transition(selected.id, "request-changes")}
                      data-testid="admin-intake-request-changes"
                      className="inline-flex items-center gap-1.5 bg-orange-100 text-orange-800 rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-orange-200"
                    >
                      <MessageCircleQuestion className="w-3.5 h-3.5" /> Request changes
                    </button>
                    <button
                      onClick={() => transition(selected.id, "reject")}
                      data-testid="admin-intake-reject"
                      className="inline-flex items-center gap-1.5 bg-rose-50 text-rose-700 rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-rose-100"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </button>
                  </div>
                </div>
              )}

              <VendorIntakeStageEditor
                stages={selected.stages || []}
                onChange={(stages) => setSelected({ ...selected, stages })}
              />
            </>
          )}
        </div>
      </div>
    </ToolPageShell>
  );
}
